import * as React from "react";
import { RouterProvider } from "react-router-dom";
import qs from "qs";
import router from "./routes";
import { getDeployment } from "./utils/deployment";

import { restoreLearner } from "./store/slices/learner";
import { useDispatch } from "./store";
import { fetchClient } from "./store/slices/client";
import { setPageTitle } from "./utils";
import LoadingSpinner from "./components/ui-components/LoadingSpinner";

const publicUrl = process.env.PUBLIC_URL;

// Valid quiz IDs for this application
const VALID_QUIZ_IDS = ["acsf3shortcourse", "acsf3", "acsf4", "vfh"];

const isValidQuizId = (id) => VALID_QUIZ_IDS.includes(id);

const App = () => {
	const { invite, quizId } = qs.parse(window.location.search, {
		ignoreQueryPrefix: true,
	});
	const dispatch = useDispatch();
	const [isInitialized, setIsInitialized] = React.useState(false);
	const [clientData, setClientData] = React.useState(null);
	const hasProcessedRouting = React.useRef(false);

	// First: Always fetch client data before any routing decisions
	React.useEffect(() => {
		const initializeClient = async () => {
			try {
				const result = await dispatch(fetchClient());
				setClientData(result);
			} catch (error) {
				console.error("Failed to fetch client data:", error);
				// Even on error, set a default to prevent blocking
				setClientData({ preferences: { defaultQuizLevel: "acsf3shortcourse" } });
			}
		};
		initializeClient();
	}, [dispatch]);

	// Second: Handle routing logic only after client data is loaded
	React.useEffect(() => {
		// Wait for client data to load
		if (clientData === null || hasProcessedRouting.current) {
			return;
		}

		const handleRouting = async () => {
			// clientData structure: { preferences: { defaultQuizLevel: "..." }, ... }
			const defaultQuizLevel = clientData?.preferences?.defaultQuizLevel || "acsf3shortcourse";

			// Case 1: User has an invite
			if (invite) {
				const inviteData = await dispatch(restoreLearner(invite));
				if (inviteData === undefined) {
					window.location.assign(`${publicUrl}/error?invalid-invite`);
					return;
				}
				
				// redirect to lln quiz if test level is not acsf3shortcourse or acsf3 or acsf4 or vfh
				const learnerTestLevel = inviteData?.learner?.testLevel;
				if (!isValidQuizId(learnerTestLevel)) {
					const deployment = getDeployment();
					const url = `https://${deployment}.quiz.lln.training?invite=${invite}`;
					window.location.href = url;
					return;
				}
								
				// redirect to new url with quiz Id if needed
				if (typeof quizId === "undefined" || !isValidQuizId(quizId) || quizId !== learnerTestLevel) {
					let pathname = window.location.pathname.replace("/short-quiz", "");
					pathname = pathname.replace("/quiz", "");
					window.location.assign(`${publicUrl}${pathname}?quizId=${learnerTestLevel}&invite=${invite}`);
					return;
				}
			} 
			// Case 2: No invite, validate or set quizId based on defaultQuizLevel
			else {
				if (typeof quizId === "undefined" || !isValidQuizId(quizId)) { 
					let pathname = window.location.pathname.replace("/short-quiz", "");
					pathname = pathname.replace("/quiz", "");
					window.location.assign(`${publicUrl}${pathname}?quizId=${defaultQuizLevel}`);
					return;
				}
			}

			// If we reach here, routing is correct - mark as initialized
			hasProcessedRouting.current = true;
			setIsInitialized(true);
		};

		handleRouting();
	}, [dispatch, invite, quizId, clientData]);

	// Set page title after routing is determined
	React.useEffect(() => {
		if (isInitialized) {
			window.document.title = setPageTitle(quizId);
		}
	}, [isInitialized, quizId]);

	// Don't render router until initialization is complete to prevent race conditions
	if (!isInitialized) {
		return <LoadingSpinner />;
	}

	return (
		<>
			<RouterProvider router={router} />
		</>
	);
};

export default App;
